import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, Loader2, PartyPopper } from 'lucide-react';
import HtmlVisual from '../digitalTwin/insight/HtmlVisual';
import ReportRatingForm, { Draft } from './ReportRatingForm';
import { CONTENT_MAX_WIDTH } from './constants';
import {
  PackagingReportDetail,
  PackagingReportSummary,
  fetchReport,
  fetchReports,
} from './api';

/**
 * `/rate-packaging` — every logged-in user scores the same packaging reports 1-5.
 *
 * Entirely data-driven: the page renders whatever `GET /packaging-rating/reports` returns, in the
 * server's sort order, so adding a report is a data load and never a code change. Nothing here
 * assumes how many there are.
 *
 * The report body is rendered by `HtmlVisual` — the product's existing sandboxed iframe (opaque
 * origin, strict CSP, no network, auto-sized to content). Reused rather than reimplemented because
 * that component carries real hard-won constraints; it also means a report must be self-contained
 * HTML, which the loader enforces.
 *
 * A user only ever sees their own score. No counts, no averages, no one else's remarks — showing
 * them would anchor whoever rates next.
 */
const PackagingRatingPage: React.FC = () => {
  const [reports, setReports] = useState<PackagingReportSummary[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [detail, setDetail] = useState<PackagingReportDetail | null>(null);

  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [bodyError, setBodyError] = useState<string | null>(null);
  const [bodyLoading, setBodyLoading] = useState(false);
  // Bumped by Retry. Re-selecting the same report id would not re-run the fetch effect on its
  // own, so the retry button needs something that actually changes.
  const [bodyAttempt, setBodyAttempt] = useState(0);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  // Set when Finish lands on the last report. Mirrors /rate's exhausted screen: once there is
  // nothing left, say so and thank them rather than leaving a dead button on the last report.
  const [finished, setFinished] = useState(false);

  const active = useMemo(
    () => reports.find((r) => r.id === activeId) ?? null,
    [reports, activeId],
  );

  const loadReports = useCallback(async () => {
    setLoading(true);
    setFatalError(null);
    try {
      const rows = await fetchReports();
      setReports(rows);
      // Land on the first thing still needing a score rather than always the top of the list.
      const firstUnrated = rows.find((r) => r.my_rating === null);
      setActiveId((current) => current ?? (firstUnrated ?? rows[0])?.id ?? null);
    } catch (e) {
      setFatalError(e instanceof Error ? e.message : 'Could not load the reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadReports(); }, [loadReports]);

  // Fetch the body separately — it is ~1 MB per report, so it is deliberately not in the list.
  useEffect(() => {
    if (activeId === null) return;
    let cancelled = false;
    setBodyLoading(true);
    setBodyError(null);
    fetchReport(activeId)
      .then((r) => { if (!cancelled) setDetail(r); })
      .catch((e) => {
        if (!cancelled) setBodyError(e instanceof Error ? e.message : 'Could not load this report');
      })
      .finally(() => { if (!cancelled) setBodyLoading(false); });
    return () => { cancelled = true; };
  }, [activeId, bodyAttempt]);

  // Mirror a successful save into the list, so the tab's ✓ updates and coming back to this report
  // shows what was saved — without refetching every report.
  const handleSaved = useCallback((reportId: number, rating: number, remarks: string) => {
    setReports((rows) => rows.map((r) => (
      r.id === reportId
        ? { ...r, my_rating: rating, my_remarks: remarks, rated_at: new Date().toISOString() }
        : r
    )));
  }, []);

  // In-progress edits per report, held by the PAGE rather than the form, so moving between reports
  // never discards typing. Necessary because a rating cannot be saved before a score is picked
  // (the column is NOT NULL), so remarks typed first have nowhere on the server to live yet.
  const handleDraftChange = useCallback((reportId: number, next: Draft) => {
    setDrafts((current) => ({ ...current, [reportId]: next }));
  }, []);

  const draftFor = useCallback((report: PackagingReportSummary): Draft => (
    drafts[report.id] ?? { rating: report.my_rating, remarks: report.my_remarks ?? '' }
  ), [drafts]);

  const isLast = reports.length > 0
    && reports.findIndex((r) => r.id === activeId) === reports.length - 1;

  const goTo = useCallback((offset: number) => {
    const index = reports.findIndex((r) => r.id === activeId);
    const next = reports[index + offset];
    if (next) setActiveId(next.id);
  }, [reports, activeId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" aria-label="Loading reports" />
      </div>
    );
  }

  if (fatalError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" aria-hidden="true" />
          <p className="text-sm text-gray-700 mb-4">{fatalError}</p>
          <button
            type="button"
            onClick={() => void loadReports()}
            className="px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (finished) {
    const rated = reports.filter((r) => r.my_rating !== null).length;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="max-w-md mx-auto text-center">
          <PartyPopper className="w-9 h-9 text-blue-600 mx-auto mb-3" aria-hidden="true" />
          <h1 className="text-lg font-semibold mb-1">Nothing left to rate</h1>
          <p className="text-gray-600 text-sm mb-6">
            You’ve worked through every packaging report available to you — {rated} of{' '}
            {reports.length} rated. Thank you!
          </p>
          {/* Unlike /rate, these reports do not go away — a rater can revisit and change a score,
              so the way back has to be offered rather than assumed. */}
          <button
            type="button"
            onClick={() => { setFinished(false); setActiveId(reports[0]?.id ?? null); }}
            className="px-5 py-2.5 rounded-xl border-[1.5px] border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:border-gray-900 hover:text-gray-900"
          >
            Review your ratings
          </button>
        </div>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="text-lg font-bold text-gray-900 mb-2">No reports to rate yet</h1>
          <p className="text-sm text-gray-600">
            There are no packaging reports available right now. Check back once one has been
            published.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto px-5 pt-7" style={{ maxWidth: CONTENT_MAX_WIDTH, paddingBottom: 220 }}>
        <header>
          <h1 className="text-xl font-bold tracking-tight text-gray-900 mb-1">
            Rate the packaging reports
          </h1>
          <p className="text-sm text-gray-600 max-w-[62ch]">
            Read each report as if a brand team had just sent it to you, then score how well it
            works as a deliverable — is the verdict clear, is it backed by evidence, could you act
            on it? Your rating is private to you.
          </p>
        </header>

        {/* Scrolls rather than wraps, so the list stays one tidy row however many reports exist. */}
        <div
          role="tablist"
          aria-label="Packaging reports"
          className="flex gap-2 mt-5 mb-3.5 overflow-x-auto pb-1"
        >
          {reports.map((report) => {
            const selected = report.id === activeId;
            const rated = report.my_rating !== null;
            return (
              <button
                key={report.id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveId(report.id)}
                className={`flex items-center gap-2.5 shrink-0 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all ${
                  selected
                    ? 'bg-gray-900 border-gray-900 text-white'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`w-[18px] h-[18px] rounded-full grid place-items-center text-[11px] font-bold ${
                    rated
                      ? 'bg-blue-600 text-white'
                      : selected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {rated ? <Check className="w-3 h-3" /> : null}
                </span>
                {report.title}
                {rated && (
                  <span className="font-normal opacity-75">· {report.my_rating}/5</span>
                )}
              </button>
            );
          })}
        </div>

        <section className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
            <span className="text-sm font-semibold text-gray-900">{active?.title}</span>
            {active?.description && (
              <span className="text-xs text-gray-500">{active.description}</span>
            )}
          </div>

          {bodyLoading && (
            <div className="h-[420px] grid place-items-center">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" aria-label="Loading report" />
            </div>
          )}

          {!bodyLoading && bodyError && (
            <div className="p-10 text-center">
              <AlertCircle className="w-7 h-7 text-red-500 mx-auto mb-3" aria-hidden="true" />
              <p className="text-sm text-gray-700 mb-4">{bodyError}</p>
              <button
                type="button"
                onClick={() => setBodyAttempt((n) => n + 1)}
                className="px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold"
              >
                Retry
              </button>
            </div>
          )}

          {/* Keyed on the report id so switching reports remounts the iframe rather than
              re-using one already sized to the previous report's height. */}
          {!bodyLoading && !bodyError && detail && detail.id === activeId && (
            <HtmlVisual key={detail.id} html={detail.html} />
          )}
        </section>
      </div>

      {/* Keyed on the report so switching remounts the form and its autosave baseline with that
          report's own values — there is no state to re-sync, and so no way to re-sync it wrongly. */}
      {active && (
        <ReportRatingForm
          key={active.id}
          report={active}
          draft={draftFor(active)}
          onDraftChange={handleDraftChange}
          onSaved={handleSaved}
          onPrev={() => goTo(-1)}
          onNext={() => (isLast ? setFinished(true) : goTo(1))}
          canGoPrev={reports.findIndex((r) => r.id === activeId) > 0}
          isLast={isLast}
        />
      )}
    </div>
  );
};

export default PackagingRatingPage;
